import { useState } from 'react';

import { Button, Icons } from '@commenda/kakashi';
import { copyToClipboard } from '@commenda/utils';

import { useNotification } from '../../../hooks/useNotification';

interface DetailRowProps {
  label: string;
  value: string;
  type?: 'text' | 'password' | 'link';
  /**
   * When true, render a copy button that copies the raw value (the underlying
   * value for password rows, not the masked dots). Off by default.
   */
  copyable?: boolean;
  copyValue?: string;
}

export function DetailRow({
  label,
  value,
  type = 'text',
  copyable = false,
  copyValue,
}: DetailRowProps) {
  const [isVisible, setIsVisible] = useState(false);
  const { setNotification } = useNotification();

  const hasValue = !!value && value !== '—';

  const displayValue =
    type === 'password' && !isVisible && hasValue ? '••••••••' : value;

  const handleCopy = () => {
    if (!hasValue) return;
    copyToClipboard(copyValue || value);
    setNotification({
      show: true,
      valid: true,
      title: 'Copied to clipboard',
      message: `${label} copied to clipboard`,
    });
  };

  return (
    <div className="grid grid-cols-2 gap-3xl py-xl border-b border-border-secondary max-w-[70%]">
      <span className="txt-small-bold text-text-primary !text-sm">{label}</span>
      <div className="flex items-center gap-sm justify-between">
        {type === 'link' ? (
          <a
            href={value}
            target="_blank"
            rel="noopener noreferrer"
            className="txt-small-regular text-text-brand-primary hover:underline !text-sm break-all"
          >
            {displayValue}
          </a>
        ) : (
          <span className="txt-small-regular text-text-primary !text-sm">
            {displayValue}
          </span>
        )}
        {hasValue && (type === 'password' || copyable) && (
          <div className="flex items-center gap-sm">
            {type === 'password' && (
              <Button
                variant="tertiary"
                onClick={() => setIsVisible((prev) => !prev)}
                size="sm"
                className="h-5 hover:bg-transparent"
              >
                {isVisible ? (
                  <Icons.EyeOff className="size-4" />
                ) : (
                  <Icons.Eye className="size-4" />
                )}
              </Button>
            )}
            {copyable && (
              <Button
                variant="tertiary"
                onClick={handleCopy}
                size="sm"
                className="h-5 hover:bg-transparent"
              >
                <Icons.Copy06 className="size-4" />
              </Button>
            )}
          </div>
        )}
      </div>
    </div>
  );
}
